"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { readPartnerBookingOrigin } from "@/utils/partner/PartnerContext";
import {
  isPartnerPath,
  isPartnerJourneyPath,
} from "@/utils/partner/partnerRoutes";

/**
 * Client gate for the global "© Sukuto. All Rights Reserved." bar.
 *
 * FinalFooter is an async Server Component mounted once in the root layout, so
 * it can't read the route itself. This thin wrapper receives the server-rendered
 * <FinalFooter /> as children and hides it on partner white-label storefronts
 * (/partner/<slug>/...), where PartnerFooter's "Powered by Sukuto" is the only
 * intended Sukuto attribution.
 *
 * The shared funnel (/cart, /booking) and the user account pages (/user/...)
 * live OUTSIDE /partner/<slug>/ but are rendered with partner branding
 * (JourneyChrome) when the checkout/session started from a storefront
 * (sukuto_partner_origin cookie). The Sukuto copyright bar must stay hidden
 * there too, so it's also suppressed on those routes when the cookie is set.
 * Everywhere else it renders unchanged, so the global site (and its SSR/SEO)
 * is untouched.
 *
 * Route classification (isPartnerPath / isPartnerJourneyPath) is shared with the
 * global origin-reset gate via @/utils/partner/partnerRoutes.
 */
const FinalFooterGate = ({ children }) => {
  const pathname = usePathname();
  // The cookie is client-only (null during SSR). Defer the cookie-based hide
  // until after mount so the server HTML and first client render match; the
  // pathname-based /partner/ hide is consistent across SSR/CSR so it stays
  // immediate.
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount flag for SSR hydration parity
  useEffect(() => setMounted(true), []);

  if (isPartnerPath(pathname)) return null;
  if (mounted && isPartnerJourneyPath(pathname) && readPartnerBookingOrigin()) {
    return null;
  }
  return children;
};

export default FinalFooterGate;
